import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Input, message } from "antd";
import { generateGroupNo } from "../../service/profile";
import "./TeamFormBody.scss";

/** 供 AutoModel `contentClassName`：样式见 `TeamFormBody.scss`（保留 AutoModel 默认内边距与 20px 圆角） */
export const TEAM_MODAL_CONTENT_CLASS = "team-modal";

const bodyWrapClass =
  "[&_.modal-text]:mb-4 [&_.modal-text]:text-sm [&_.modal-text]:leading-normal [&_.modal-text]:text-[#595959] " +
  "[&_.modal-question]:mt-4 [&_.modal-question]:text-center [&_.modal-question]:text-sm [&_.modal-question]:font-medium [&_.modal-question]:text-[#262626] " +
  "[&_.modal-hint]:rounded [&_.modal-hint]:border [&_.modal-hint]:border-[#e8e8e8] [&_.modal-hint]:bg-[#f6f6f6] [&_.modal-hint]:px-3 [&_.modal-hint]:py-2 [&_.modal-hint]:text-xs [&_.modal-hint]:leading-snug [&_.modal-hint]:text-[#8c8c8c]";

const textInputClass =
  "mb-3 h-10 w-full rounded border border-[#d9d9d9] text-sm hover:border-[#40a9ff] focus:border-[#40a9ff] focus:shadow-[0_0_0_2px_rgba(24,144,255,0.2)] " +
  "placeholder:text-[#bfbfbf]";

const readOnlyInputClass =
  "mb-3 h-10 w-full cursor-not-allowed rounded border border-[#d9d9d9] bg-[#f5f5f5] text-sm text-[#595959] focus:border-[#d9d9d9] focus:shadow-none";

/** 退出团队：行内只读标签（不用 ant Input，避免与文案基线不齐、宽度抖动） */
const exitChipClass =
  "mx-1 inline-flex max-w-[min(100%,14rem)] items-center rounded-md border border-[#d9d9d9] bg-[#f5f5f5] px-2.5 py-0.5 align-middle text-sm font-medium text-[#262626] [overflow-wrap:anywhere]";

export type TeamFormMode = "create" | "join" | "exit";

export interface TeamFormBodyProps {
  visible: boolean;
  type: TeamFormMode;
  onConfirm: (teamId?: string, teamName?: string) => void;
  currentTeamId?: string;
  auditorName?: string;
}

export type TeamFormBodyRef = {
  submit: () => void;
  reset: () => void;
};

export function teamFormTitle(type: TeamFormMode): string {
  switch (type) {
    case "create":
      return "创建团队";
    case "join":
      return "加入团队";
    case "exit":
      return "退出团队";
    default:
      return "团队操作";
  }
}

export const TeamFormBody = forwardRef<TeamFormBodyRef, TeamFormBodyProps>(
  ({ visible, type, onConfirm, currentTeamId = "", auditorName = "" }, ref) => {
    const onConfirmRef = useRef(onConfirm);
    onConfirmRef.current = onConfirm;

    const [teamId, setTeamId] = useState("");
    const [generatedGroupNo, setGeneratedGroupNo] = useState("");

    useEffect(() => {
      if (visible && type === "create") {
        generateGroupNo()
          .then((groupNo) => {
            setGeneratedGroupNo(groupNo);
          })
          .catch((error) => {
            console.error("生成团队编号失败:", error);
            message.error("生成团队编号失败，请重试");
          });
      }
    }, [visible, type]);

    const reset = () => {
      setTeamId("");
      setGeneratedGroupNo("");
    };

    const submit = () => {
      if (type === "create") {
        if (!generatedGroupNo) {
          message.error("团队编号生成中，请稍候");
          return;
        }
        onConfirmRef.current(generatedGroupNo, generatedGroupNo);
      } else if (type === "join") {
        if (!teamId.trim()) {
          message.error("请输入团队名称");
          return;
        }
        onConfirmRef.current(teamId.trim());
      } else if (type === "exit") {
        onConfirmRef.current(currentTeamId);
      }
    };

    useImperativeHandle(ref, () => ({ submit, reset }), [type, generatedGroupNo, teamId, currentTeamId]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        submit();
      }
    };

    const renderContent = () => {
      switch (type) {
        case "create":
          return (
            <div>
              <div className="modal-text">团队编号</div>
              <Input
                value={generatedGroupNo}
                disabled
                placeholder="团队编号自动生成中..."
                className={readOnlyInputClass}
                autoFocus
              />
            </div>
          );

        case "join":
          return (
            <div>
              <div className="modal-text">请输入团队名称</div>
              <Input
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="请输入团队名称"
                className={textInputClass}
                autoFocus
              />
            </div>
          );

        case "exit": {
          const teamLabel = (currentTeamId || "").trim() || "—";
          const auditorLabel = (auditorName || "").trim() || "—";
          return (
            <div className="team-exit-copy text-sm leading-relaxed text-[#595959]">
              <p className="team-exit-copy__line m-0 mb-3 last:mb-0">
                您目前所在的团队为
                <span className={exitChipClass} title={teamLabel}>
                  {teamLabel}
                </span>
              </p>
              <p className="team-exit-copy__line m-0">
                审核者为
                <span className={exitChipClass} title={auditorLabel}>
                  {auditorLabel}
                </span>
                ，是否确认退出？
              </p>
            </div>
          );
        }

        default:
          return null;
      }
    };

    return <div className={bodyWrapClass}>{renderContent()}</div>;
  }
);

TeamFormBody.displayName = "TeamFormBody";
